// MetricsTable.jsx

import React, { useState, useMemo, useRef, useEffect } from 'react';
import MetricsRow from './MetricsRow';
import ExcelUploader from './ExcelUploader';
import '../css/MetricsTable.css'; // Import the relevant styles
import { db } from '../config/firebase';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';

function MetricsTable({
  people = [],
  dailyMetricsData = [],
  selectedNationality,
  selectedFirm,
  selectedYear,
  selectedMonth,
  refreshData,
  firms = [],
  updateDailyMetrics, // Function to update metrics
  metrickePostavke = {}, // Object with metric settings
}) {
  const daysInMonth = 35;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const [uploadedDates, setUploadedDates] = useState({});

  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const tableRef = useRef(null);

  // Debugging - ispiši sve osobe i njihov isPinned status
  useEffect(() => {
    console.log('People data:', people.map(p => ({ id: p.id, name: p.name, isPinned: p.isPinned })));
  }, [people]);

  // Synchronize scroll between top and bottom
  const syncScroll = (source) => {
    if (source === 'top' && topScrollRef.current && bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    } else if (source === 'bottom' && topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
  };

  // Toggle pin status for a user
  const togglePinUser = async (userId) => {
    try {
      const person = people.find(p => p.id === userId);
      if (!person) return;

      console.log('Prije ažuriranja:', { userId, currentPinStatus: person.isPinned });
      
      const newPinStatus = !(person.isPinned === true);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isPinned: newPinStatus
      });

      console.log('Nakon ažuriranja:', { userId, newPinStatus });

      // Osvježi podatke nakon promjene
      refreshData();
    } catch (error) {
      console.error('Greška pri ažuriranju pin statusa:', error);
      alert('Došlo je do greške pri ažuriranju pin statusa.');
    }
  };

  // Filter and sort people
  const filteredPeople = useMemo(() => {
    let filtered = [...people];

    // Filtriranje po nacionalnosti
    if (selectedNationality !== 'Sve') {
      filtered = filtered.filter((person) => person.nationality === selectedNationality);
    }

    // Filtriranje po firmi
    if (selectedFirm) {
      filtered = filtered.filter((person) => person.firmId === selectedFirm);
    }

    // Filtriranje arhiviranih osoba
    filtered = filtered.filter((person) => {
      if (person.archived) {
        const archiveDate = new Date(person.archiveYear, person.archiveMonth - 1);
        const selectedDate = new Date(selectedYear, selectedMonth - 1);
        return archiveDate > selectedDate;
      }
      return true;
    });

    // Filtriranje po početnom datumu
    filtered = filtered.filter((person) => {
      const startDate = new Date(person.startYear, person.startMonth - 1);
      const selectedDate = new Date(selectedYear, selectedMonth - 1);
      return startDate <= selectedDate;
    });

    // Sortiranje - prvo pinani korisnici, zatim po firmi
    return filtered.sort((a, b) => {
      // Prvo provjeri jesu li pinani (eksplicitno provjeri boolean vrijednost)
      const isPinnedA = a.isPinned === true;
      const isPinnedB = b.isPinned === true;
      
      if (isPinnedA && !isPinnedB) return -1;
      if (!isPinnedA && isPinnedB) return 1;
      
      // Ako su oba pinana ili oba nisu pinana, sortiraj po firmi
      const firmA = firms.find(f => f.id === a.firmId)?.name || '';
      const firmB = firms.find(f => f.id === b.firmId)?.name || '';
      return firmA.localeCompare(firmB);
    });
  }, [people, selectedNationality, selectedFirm, selectedYear, selectedMonth, firms]);

  // Calculate total and daily sums, and averages per person
  const {
    totalSum,
    totalActualWorkingHours,
    dailyAverageAcrossAllDays,
    dailySums,
    dailyAverages,
    nonZeroSumDays, // Count of non-zero days across all people
    sumOfNonZeroDays, // Sum of values for non-zero days
    averageSumPerPerson,
    overallAverage,
    averageTotalWorkingHoursPerPerson,
  } = useMemo(() => {
    let total = 0; // Total sum across all people and all days
    let totalWorkingHours = 0; // Total working hours sum
    const dailyTotals = Array(daysInMonth).fill(0); // Holds the sum for each day
    const personTotals = [];
    const averages = [];
    const personTotalWorkingHoursArray = [];
    let sumOfNonZeroDays = 0; // Sum of only non-zero days
    let nonZeroSumDays = 0; // Count of non-zero sum days

    filteredPeople.forEach((person) => {
      const personMetrics = dailyMetricsData.filter(
        (metric) =>
          metric.userId === person.id &&
          metric.year === selectedYear &&
          metric.month === selectedMonth
      );

      const personTotal = personMetrics.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
      personTotals.push(personTotal);

      const daysWorked = personMetrics.filter(
        (m) => m.amount !== undefined && m.amount !== null
      ).length;
      const averageEarnings = daysWorked > 0 ? personTotal / daysWorked : 0;
      averages.push(averageEarnings);

      const personTotalWorkingHours = personMetrics.reduce((sum, m) => {
        const hours = parseFloat(m.actualWorkingHours) || 0;
        return sum + hours;
      }, 0);
      totalWorkingHours += personTotalWorkingHours;
      personTotalWorkingHoursArray.push(personTotalWorkingHours);

      personMetrics.forEach((metric) => {
        const dayIndex = metric.day - 1;
        const amount = parseFloat(metric.amount) || 0;
        dailyTotals[dayIndex] += amount; // Accumulate sum for the day
        if (amount > 0) {
          total += amount; // Add to the overall total sum
        }
      });
    });

    // Calculate the number of non-zero sum days and the sum of those days
    dailyTotals.forEach((sum) => {
      if (sum > 0) {
        nonZeroSumDays += 1; // Count non-zero day
        sumOfNonZeroDays += sum; // Add to the sum of non-zero days
      }
    });

    // Calculate daily averages
    const dailyAverages = dailyTotals.map((sum) =>
      sum > 0 ? sum / filteredPeople.length : 0
    );

    const dailyAverageAcrossAllDays =
      nonZeroSumDays > 0 ? sumOfNonZeroDays / nonZeroSumDays : 0;

    const averageSumPerPerson =
      filteredPeople.length > 0 ? total / filteredPeople.length : 0;

    const overallAverage =
      averages.length > 0 ? averages.reduce((sum, avg) => sum + avg, 0) / averages.length : 0;

    const averageTotalWorkingHoursPerPerson =
      personTotalWorkingHoursArray.length > 0
        ? personTotalWorkingHoursArray.reduce((sum, hours) => sum + hours, 0) /
          personTotalWorkingHoursArray.length
        : 0;

    return {
      totalSum: total, // Overall total sum across all people and all days
      totalActualWorkingHours: totalWorkingHours,
      dailySums: dailyTotals, // Sum for each day
      dailyAverages, // Average for each day
      nonZeroSumDays, // Count of non-zero sum days
      sumOfNonZeroDays, // Sum of values for non-zero sum days
      dailyAverageAcrossAllDays, // Average sum across non-zero days
      averageSumPerPerson,
      overallAverage,
      averageTotalWorkingHoursPerPerson,
    };
  }, [filteredPeople, dailyMetricsData, selectedYear, selectedMonth, daysInMonth]);

  // Funkcija za parsiranje datuma iz imena filea
  const extractDateFromFilename = (filename) => {
    // Traži pattern dd.mm ili dd.mm.yyyy
    const datePattern = /(\d{1,2})\.(\d{1,2})(\.(\d{4}))?/;
    const match = filename.match(datePattern);
    
    if (match) {
      const day = parseInt(match[1]);
      const month = parseInt(match[2]);
      const year = match[4] ? parseInt(match[4]) : selectedYear;
      
      return { day, month, year };
    }
    return null;
  };

  // Funkcija za dobivanje dana u tjednu
  const getDayOfWeek = (day, month, year) => {
    const date = new Date(year, month - 1, day);
    const daysOfWeek = ['Ned', 'Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub'];
    return daysOfWeek[date.getDay()];
  };

  // Dohvati informacije o uploadanim danima
  useEffect(() => {
    const fetchUploadedDays = async () => {
      try {
        const uploadedDaysRef = collection(db, 'uploadedDays');
        const q = query(
          uploadedDaysRef,
          where('year', '==', selectedYear),
          where('month', '==', selectedMonth)
        );
        
        const querySnapshot = await getDocs(q);
        const dates = {};
        
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const extractedDate = extractDateFromFilename(data.filename);
          
          if (extractedDate && extractedDate.month === selectedMonth) {
            dates[data.day] = {
              filename: data.filename,
              date: extractedDate,
              dayOfWeek: getDayOfWeek(extractedDate.day, extractedDate.month, extractedDate.year)
            };
          }
        });
        
        setUploadedDates(dates);
      } catch (error) {
        console.error('Greška pri dohvaćanju uploadanih dana:', error);
      }
    };

    fetchUploadedDays();
  }, [selectedYear, selectedMonth]);

  // Funkcija za formatiranje datuma
  const formatDate = (day, month) => {
    return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}`;
  };

  return (
    <div className="metrics-table-container">
      <div className="table-header">
        <span className="people-count">
          Prikazano osoba: {filteredPeople.length}
        </span>
      </div>
      <div className="table-wrapper">
        <table className="metrics-table" ref={tableRef}>
          <thead>
            <tr className="summary-row">
              <td>
                <strong>Suma</strong>
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <strong>{totalSum.toFixed(2)}</strong>
              </td>

              <td></td>
              {dailySums.map((sum, index) => (
                <td key={index}>
                  <strong>{sum.toFixed(2)}</strong>
                </td>
              ))}
            </tr>
            <tr className="average-sum-row">
              <td>
                <strong>Prosjek Suma</strong>
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <strong>{dailyAverageAcrossAllDays.toFixed(2)}</strong>
              </td>
              <td>
                <strong>{averageSumPerPerson.toFixed(2)}</strong>
              </td>

              <td>
                <strong>{overallAverage.toFixed(2)}</strong>
              </td>
              {dailySums.map((_, index) => (
                <td key={index}></td>
              ))}
            </tr>
            <tr>
              <th>Ime</th>
              <th>PIN</th>
              <th>Rider ID</th>
              <th>Grad</th>
              <th>Firma</th>
              <th>Nacionalnost</th>
              <th>Suma</th>

              <th>Dnevni Prosjek</th>
              {days.map((day) => (
                <th key={day} className="day-header">
                  <div>Dan {day}</div>
                  {uploadedDates[day] && (
                    <div className="date-info">
                      <div>{uploadedDates[day].dayOfWeek}</div>
                      <div>{formatDate(uploadedDates[day].date.day, uploadedDates[day].date.month)}</div>
                    </div>
                  )}
                  <ExcelUploader
                    onDataParsed={(data, filename) => {
                      updateDailyMetrics(day, data, filename);
                    }}
                  >
                    {uploadedDates[day] ? 'Promijeni' : 'Upload'}
                  </ExcelUploader>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredPeople.map((person) => {
              // Debug log za svaku osobu
              console.log('Rendering person:', { 
                id: person.id, 
                name: person.name, 
                isPinned: person.isPinned,
                isPinnedType: typeof person.isPinned 
              });
              
              return (
                <MetricsRow
                  key={person.id}
                  person={person}
                  dailyMetricsData={dailyMetricsData.filter(
                    (metric) =>
                      metric.userId === person.id &&
                      metric.year === selectedYear &&
                      metric.month === selectedMonth
                  )}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  refreshData={refreshData}
                  firms={firms}
                  metrickePostavke={metrickePostavke}
                  daysInMonth={daysInMonth}
                  isPinned={person.isPinned === true}
                  onTogglePin={() => togglePinUser(person.id)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MetricsTable;
